import React, { useEffect, useMemo, useRef } from 'react';
import {
  Route,
  useLocation,
  useHistory,
  Redirect,
  useRouteMatch,
} from 'react-router-dom-v5';
import { SwitchWithNotFound } from '@/src/components';
import {
  CHARGE_ANYTIME_PAYG_A_B_TEST_PPM_V_KWH,
  DRIVE_ANYTIME,
  CHARGE_ANYTIME_REINSTATE,
} from '@/src/constants/feature-flags';
import {
  OptimizelyFeature,
  useDecision,
  useFeature,
} from '@/src/utils/optimizely';
import {
  ROUTE_ANYTIME_ELIGIBILITY,
  ROUTE_CHARGE_ANYTIME_ELIGIBILITY,
} from '@/src/constants/routes';
import { useAccountContext, useCustomerContext } from '@ovotech/ui-tools';
import { useSelector } from 'react-redux';
import { hasSmartMeter as hasSmartMeterSelector } from '@/src/pages/Renewal/selectors';
import { Loading } from '@/src/pages/AnytimeEligibilityV2/components/Loading';
import { fetchFn } from '@/src/pages/AnytimeEligibilityV2/services/chargeAnytimeAPI';
import { ChargeAnytimeProvider } from '@monovo/ev-anytime';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import { SmartChargerQuestions } from '@/src/pages/AnytimeEligibilityV2/pages/SmartChargerQuestions/SmartChargerQuestions';
import { VehicleAndHomeSetupQuestions } from '@/src/pages/AnytimeEligibilityV2/pages/VehicleAndHomeSetupQuestions/VehicleAndHomeSetupQuestions';
import { Fallout } from '@/src/pages/AnytimeEligibilityV2/pages/Fallout/Fallout';
import { VolvoAnytimeAppLink } from '@/src/pages/AnytimeEligibilityV2/pages/VolvoAnytimeAppLink/VolvoAnytimeAppLink';
import { HyundaiAnytimeAppLink } from '@/src/pages/AnytimeEligibilityV2/pages/HyundaiAnytimeAppLink/HyundaiAnytimeAppLink';
import { KiaAnytimeAppLink } from '@/src/pages/AnytimeEligibilityV2/pages/KiaAnytimeAppLink/KiaAnytimeAppLink';
import { DataSharingAndTCConsent } from '@/src/pages/AnytimeEligibilityV2/pages/DataSharingAndTCConsent/DataSharingAndTCConsent';
import { Introduction } from '@/src/pages/AnytimeEligibilityV2/pages/Introduction/Introduction';
import { ChargeAnytimeDisconnect } from '@/src/pages/AnytimeEligibilityV2/pages/ChargeAnytimeDisconnect/ChargeAnytimeDisconnect';
import { ProductSelection } from './pages/ProductSelection/ProductSelection';
import { SelectedPlan } from './pages/SelectedPlan/SelectedPlan';
import { ChargeAnytimeSwitch } from '@/src/pages/AnytimeEligibilityV2/pages/ChargeAnytimeSwitch/ChargeAnytimeSwitch';
import { SwitchConfirmationPage } from '@/src/pages/AnytimeEligibilityV2/pages/SwitchConfirmationPage/SwitchConfirmationPage';
import { SwitchFallout } from '@/src/pages/AnytimeEligibilityV2/pages/SwitchFallout/SwitchFallout';
import { OvoChargeAppLink } from '@/src/pages/AnytimeEligibilityV2/pages/OvoChargeAppLink/OvoChargeAppLink';
import { ChargeAnytimeReactivate } from '@/src/pages/AnytimeEligibilityV2/pages/ChargeAnytimeReactivate/ChargeAnytimeReactivate';

export const routes = {
  disconnect: '/disconnect',
};

export const ChargeAnytime = () => {
  const { path } = useRouteMatch();
  const { pathname } = useLocation();
  const hasSmartMeter = useSelector(hasSmartMeterSelector) as boolean;
  const [decision] = useDecision(CHARGE_ANYTIME_PAYG_A_B_TEST_PPM_V_KWH);
  const showKwhVariant = decision.variationKey === 'price_per_kwh';
  const { customerId } = useCustomerContext();
  const { accountId } = useAccountContext();

  const positions = useRef(Object.create(null));

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (pathname in positions.current) {
      const { x, y } = positions.current[pathname];
      window.scrollTo(x, y);
    } else {
      window.scrollTo(0, 0);
    }

    const handler = () => {
      positions.current[pathname] = { x: window.scrollX, y: window.scrollY };
    };
    window.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, [pathname]);

  const disconnectRoute = `${path}${routes.disconnect}`;
  const [isReinstateEnabled] = useFeature(CHARGE_ANYTIME_REINSTATE);

  const hasAnytimeAllowedRoutes = useMemo(
    () => [
      ROUTE_ANYTIME_ELIGIBILITY.OVO_CHARGE_APP,
      ROUTE_ANYTIME_ELIGIBILITY.VOLVO_APP,
      ROUTE_ANYTIME_ELIGIBILITY.HYUNDAI_APP,
      ROUTE_ANYTIME_ELIGIBILITY.KIA_APP,
      ROUTE_ANYTIME_ELIGIBILITY.SWITCH,
      ROUTE_ANYTIME_ELIGIBILITY.SWITCH_CONFIRMATION,
      ROUTE_ANYTIME_ELIGIBILITY.SWITCH_FALLOUT,
      ROUTE_ANYTIME_ELIGIBILITY.REACTIVATE,
      disconnectRoute,
    ],
    [disconnectRoute],
  );
  const {
    payAsYouDriveBoltonActive,
    payAsYouDriveBoltonInitialised,
    hasActivePayMonthlyBolton,
    hasInitializedPayMonthlyBolton,
    isLoading,
    refetchPlanDetails,
  } = useChargeAnytimeBoltonState();

  const isBoltonInitializedOrActive =
    payAsYouDriveBoltonActive ||
    payAsYouDriveBoltonInitialised ||
    hasActivePayMonthlyBolton ||
    hasInitializedPayMonthlyBolton;

  useEffect(() => {
    const concludedPagePaths = [
      ROUTE_ANYTIME_ELIGIBILITY.OVO_CHARGE_APP,
      ROUTE_ANYTIME_ELIGIBILITY.VOLVO_APP,
      ROUTE_ANYTIME_ELIGIBILITY.HYUNDAI_APP,
      ROUTE_ANYTIME_ELIGIBILITY.KIA_APP,
      ROUTE_ANYTIME_ELIGIBILITY.FALLOUT,
      ROUTE_ANYTIME_ELIGIBILITY.SWITCH_FALLOUT,
      ROUTE_ANYTIME_ELIGIBILITY.REACTIVATE,
      disconnectRoute,
    ];

    if (
      isBoltonInitializedOrActive &&
      !concludedPagePaths.includes(location.pathname) &&
      !hasAnytimeAllowedRoutes.includes(location.pathname)
    ) {
      history.replace(ROUTE_ANYTIME_ELIGIBILITY.FALLOUT);
    }
  }, [
    isBoltonInitializedOrActive,
    history,
    location,
    disconnectRoute,
    hasAnytimeAllowedRoutes,
  ]);

  useEffect(() => {
    refetchPlanDetails();
  }, []);

  const isLoadingCA = isLoading || !accountId || !customerId;
  const canReenterJourney =
    hasAnytimeAllowedRoutes.includes(pathname) ||
    (!payAsYouDriveBoltonActive &&
      !payAsYouDriveBoltonInitialised &&
      !hasActivePayMonthlyBolton &&
      !hasInitializedPayMonthlyBolton);

  if (isLoadingCA) {
    return <Loading />;
  }

  return (
    <OptimizelyFeature feature={DRIVE_ANYTIME}>
      {isEnabled => {
        return (
          isEnabled && (
            <ChargeAnytimeProvider
              hasSmartMeter={hasSmartMeter}
              chargeAnytimeApi={{
                fetch: fetchFn,
              }}
              showKwhVariant={showKwhVariant}
              accountId={accountId}
              customerId={customerId}
            >
              {!canReenterJourney ? (
                <Fallout />
              ) : (
                <SwitchWithNotFound>
                  <Redirect
                    exact={true}
                    from={ROUTE_CHARGE_ANYTIME_ELIGIBILITY}
                    to={
                      canReenterJourney
                        ? ROUTE_ANYTIME_ELIGIBILITY.INTRODUCTION
                        : ROUTE_ANYTIME_ELIGIBILITY.FALLOUT
                    }
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.INTRODUCTION}
                    component={Introduction}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.SMART_CHARGER}
                    component={SmartChargerQuestions}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.VEHICLE_AND_HOME_SETUP}
                    component={VehicleAndHomeSetupQuestions}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.CONSENT}
                    component={DataSharingAndTCConsent}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.OVO_CHARGE_APP}
                    component={OvoChargeAppLink}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.VOLVO_APP}
                    component={VolvoAnytimeAppLink}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.HYUNDAI_APP}
                    component={HyundaiAnytimeAppLink}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.KIA_APP}
                    component={KiaAnytimeAppLink}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.FALLOUT}
                    component={Fallout}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.SWITCH}
                    component={ChargeAnytimeSwitch}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.SWITCH_CONFIRMATION}
                    component={SwitchConfirmationPage}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.SWITCH_FALLOUT}
                    component={SwitchFallout}
                  />
                  <Route
                    exact
                    path={disconnectRoute}
                    component={ChargeAnytimeDisconnect}
                  />
                  <Route
                    exact
                    path={ROUTE_ANYTIME_ELIGIBILITY.REACTIVATE}
                    render={() =>
                      isReinstateEnabled ? (
                        <ChargeAnytimeReactivate />
                      ) : (
                        <Redirect to={ROUTE_ANYTIME_ELIGIBILITY.FALLOUT} />
                      )
                    }
                  />
                  <Route
                    exact
                    path={`${ROUTE_ANYTIME_ELIGIBILITY.PRODUCT_SELECTION}`}
                    component={ProductSelection}
                  />
                  <Route
                    exact
                    path={`${ROUTE_ANYTIME_ELIGIBILITY.SELECTED_PLAN}`}
                    component={SelectedPlan}
                  />
                  <Redirect to={ROUTE_ANYTIME_ELIGIBILITY.INTRODUCTION} />
                </SwitchWithNotFound>
              )}
            </ChargeAnytimeProvider>
          )
        );
      }}
    </OptimizelyFeature>
  );
};

export default ChargeAnytime;
